// FichaActivoModal.js — ficha completa del activo (Prioridad 1.4): marca,
// modelo, nº de serie, garantía/contrato, historial de intervenciones y
// galería de fotos permanente (distinta de las fotos de la visita del día).
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { authService } from '../../services/authService';
import { useSugerenciasPieza } from '../../hooks/useSugerenciasPieza';

function fmtFecha(f) {
  if (!f) return '—';
  const [y, m, d] = String(f).slice(0, 10).split('-');
  return d && m && y ? `${d}/${m}/${y}` : f;
}

// Mismo criterio que comprimirImagen() de FinalizarVisitaModal.js — reduce una
// foto de móvil a un tamaño razonable antes de subirla.
function comprimirImagen(file, maxLado = 1600, calidad = 0.8) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const escala = Math.min(1, maxLado / Math.max(img.width, img.height));
      const w = Math.round(img.width * escala);
      const h = Math.round(img.height * escala);
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      canvas.getContext('2d').drawImage(img, 0, 0, w, h);
      resolve(canvas.toDataURL('image/jpeg', calidad).split(',')[1]);
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('No se pudo leer la imagen')); };
    img.src = url;
  });
}

export default function FichaActivoModal({ activoId, ordenId, clienteId, onClose }) {
  const [ficha, setFicha] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [subiendo, setSubiendo] = useState(false);
  const fileInputRef = useRef(null);
  // Mismo filtro (equipo exacto por serie / modelo) que el buscador de
  // material en Finalizar Visita — aquí es solo informativo, no añade nada
  // a ninguna orden (Rafa, 2026-11-07).
  const { sugerenciasEquipo, sugerenciasModelo, sugerenciasDespieceOficial, cargando: cargandoSugerencias } = useSugerenciasPieza(activoId);
  const [paginaPiezas, setPaginaPiezas] = useState(0);
  const PIEZAS_POR_PAGINA = 10;
  // Lista única (sin "este equipo"/"este modelo" — aquí es solo un despiece
  // de referencias, da igual de dónde salió cada una), con el despiece
  // oficial de fábrica como último recurso cuando no hay historial de uso
  // real (Rafa, 2026-11-04).
  const piezasSugeridas = [
    ...sugerenciasEquipo,
    ...sugerenciasModelo.filter(p => !sugerenciasEquipo.some(e => e.id === p.id)),
    ...sugerenciasDespieceOficial.filter(p =>
      !sugerenciasEquipo.some(e => e.id === p.id) && !sugerenciasModelo.some(m => m.id === p.id)),
  ];
  useEffect(() => { setPaginaPiezas(0); }, [activoId]);

  const cargar = useCallback(() => {
    setLoading(true); setError('');
    const params = new URLSearchParams({ activoId, ...(ordenId ? { ordenId } : {}), ...(clienteId ? { clienteId } : {}) });
    fetch(`/.netlify/functions/getFichaActivo?${params}`, { headers: { ...authService.authHeader() } })
      .then(r => r.json())
      .then(data => { if (!data.success) { setError(data.error); return; } setFicha(data); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [activoId, ordenId, clienteId]);

  useEffect(() => { cargar(); }, [cargar]);

  const handleSubirFoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setSubiendo(true); setError('');
    try {
      const base64 = await comprimirImagen(file);
      const res = await fetch('/.netlify/functions/subirImagenActivo', {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({ activoId, ordenId, base64, contentType: 'image/jpeg' }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error);
      cargar();
    } catch (e) {
      setError(e.message);
    } finally {
      setSubiendo(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center">
      <div className="bg-white rounded-t-3xl sm:rounded-3xl shadow-xl w-full sm:max-w-lg max-h-[88vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3.5 border-b">
          <h3 className="font-bold text-gray-900">Ficha del activo</h3>
          <button data-attr="ficha-activo-cerrar" onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none px-1">×</button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {loading && <div className="flex justify-center py-10"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-500" /></div>}
          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-2 text-sm">{error}</div>}

          {!loading && ficha && (
            <>
              <div>
                <p className="font-bold text-gray-900 text-lg">{[ficha.activo.marca, ficha.activo.modelo].filter(Boolean).join(' ') || 'Equipo'}</p>
                <div className="text-sm text-gray-600 mt-1 space-y-0.5">
                  {ficha.activo.numeroSerie && <p className="font-mono">Nº serie: {ficha.activo.numeroSerie}</p>}
                  {ficha.activo.fechaInstalacion && <p>Instalado: {fmtFecha(ficha.activo.fechaInstalacion)}</p>}
                  {ficha.activo.fechaGarantia && <p>Garantía hasta: {fmtFecha(ficha.activo.fechaGarantia)}</p>}
                  {ficha.activo.sujetoMantenimiento && <p className="text-emerald-600 font-semibold">✓ Sujeto a contrato de mantenimiento</p>}
                </div>
              </div>

              {piezasSugeridas.length > 0 && (
                <div>
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Piezas que se suelen usar en este equipo</p>
                  <div className="space-y-1.5">
                    {piezasSugeridas
                      .slice(paginaPiezas * PIEZAS_POR_PAGINA, (paginaPiezas + 1) * PIEZAS_POR_PAGINA)
                      .map(p => (
                        <div key={p.id} className="flex items-center gap-2 bg-gray-50 border border-gray-100 rounded-xl px-3 py-2 text-sm">
                          <span className="font-mono font-semibold text-gray-800 flex-shrink-0">{p.referencia}</span>
                          <span className="text-gray-500 truncate flex-1">{p.nombre}</span>
                        </div>
                      ))}
                  </div>
                  {piezasSugeridas.length > PIEZAS_POR_PAGINA && (
                    <div className="flex items-center justify-between mt-2">
                      <button
                        type="button"
                        onClick={() => setPaginaPiezas(p => Math.max(0, p - 1))}
                        disabled={paginaPiezas === 0}
                        className="text-xs font-semibold text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed px-2 py-1"
                      >← Anteriores</button>
                      <span className="text-xs text-gray-400">
                        {paginaPiezas * PIEZAS_POR_PAGINA + 1}-{Math.min((paginaPiezas + 1) * PIEZAS_POR_PAGINA, piezasSugeridas.length)} de {piezasSugeridas.length}
                      </span>
                      <button
                        type="button"
                        onClick={() => setPaginaPiezas(p => ((p + 1) * PIEZAS_POR_PAGINA < piezasSugeridas.length ? p + 1 : p))}
                        disabled={(paginaPiezas + 1) * PIEZAS_POR_PAGINA >= piezasSugeridas.length}
                        className="text-xs font-semibold text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed px-2 py-1"
                      >Siguientes →</button>
                    </div>
                  )}
                </div>
              )}
              {!cargandoSugerencias && piezasSugeridas.length === 0 && (
                <p className="text-xs text-gray-400">Sin piezas registradas todavía para este equipo/modelo</p>
              )}

              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Fotos del equipo</p>
                  <button data-attr="ficha-activo-anadir-foto" onClick={() => fileInputRef.current?.click()} disabled={subiendo} className="text-xs font-semibold text-blue-600 disabled:opacity-50">
                    {subiendo ? 'Subiendo...' : '+ Añadir foto'}
                  </button>
                  <input ref={fileInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleSubirFoto} />
                </div>
                {ficha.activo.imagenes.length > 0 ? (
                  <div className="grid grid-cols-3 gap-2">
                    {ficha.activo.imagenes.map((img, i) => (
                      <a data-attr="ficha-activo-foto-abrir" key={i} href={img.url} target="_blank" rel="noopener noreferrer">
                        <img src={img.url} alt="" className="w-full h-24 object-cover rounded-xl" />
                      </a>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400">Sin fotos todavía</p>
                )}
              </div>

              <div className="ph-no-capture">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Historial de intervenciones</p>
                {ficha.historial.length > 0 ? (
                  <div className="space-y-2">
                    {ficha.historial.map(h => (
                      <div key={h.id} className="bg-gray-50 rounded-xl px-3 py-2.5 text-sm">
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-gray-800">{h.referencia}</span>
                          <span className="text-xs text-gray-400">{fmtFecha(h.fecha)}</span>
                        </div>
                        {h.tecnico && <p className="text-xs text-gray-500 mt-0.5">👷 {h.tecnico}</p>}
                        {h.diagnostico && <p className="text-xs text-gray-600 mt-1">{h.diagnostico}</p>}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400">Sin intervenciones anteriores registradas</p>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
